import classNames from 'classnames';
import { getHeadlessEngine } from 'libs/coveo/getHeadlessEngine';
import PropTypes from 'prop-types';
import React from 'react';

import { MainContent } from './components/MainContent';
import { Typeahead } from './components/Typeahead';
import { TermFinderProvider } from './stores/termFinderStore';
import styles from './TermFinder.module.scss';

/**
 * The Term Finder component provides search capabilities and presents search
 * results. It supports a search bar with typeahead and synonym functionality.
 * It is able to curate a list of search results based off the searched term,
 * so that information can be easily found by the user.
 *
 * This molecule component uses:
 * - Typeahead
 * - Accordion
 * - LinkListItem
 */
export const TermFinder = (props) => {
    const {
        id,
        className,
        label,
        eyebrow,
        showResultsButtonLabel,
        hideResultsButtonLabel,

        noResultsComponent,
        errorComponent,

        accordionItems,

        coveo,
    } = props;

    const headlessEngine = getHeadlessEngine(`term-finder-${id}`, {
        ...coveo,
        analytics: { analyticsMode: 'legacy', trackingId: 'term-finder' },
    });

    const termFinderProviderProps = {
        headlessEngine,

        accordionItems,

        label,
        eyebrow,
        showResultsButtonLabel,
        hideResultsButtonLabel,

        noResultsComponent,
        errorComponent,
    };
    const analyticObj = {
        eventName: 'search',
        componentName: 'Term Finder',
        componentTitle: eyebrow,
    };

    return (
        <TermFinderProvider {...termFinderProviderProps}>
            <div className={classNames(styles['term-finder'], className)}>
                <Typeahead analytics={analyticObj} />
                <MainContent analytics={analyticObj} />
            </div>
        </TermFinderProvider>
    );
};

TermFinder.propTypes = {
    /**
     * Typeahead label
     */
    label: PropTypes.string,
    /**
     * Eyebrow for results section
     */
    eyebrow: PropTypes.string,
    /**
     * Button label for the view all results
     */
    showResultsButtonLabel: PropTypes.string,
    /**
     * Button label for the hide all results
     */
    hideResultsButtonLabel: PropTypes.string,
    /**
     * Url to fetch suggestions
     */
    suggestionsUrl: PropTypes.string,
    /**
     * No Results XF
     */
    noResultsComponent: PropTypes.element,
    /**
     * Error XF
     */
    errorComponent: PropTypes.element,
    /**
     * Accordion items
     */
    accordionItems: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            links: PropTypes.arrayOf(PropTypes.object).isRequired,
            disabled: PropTypes.bool,
        }),
    ).isRequired,
    /**
     * Url to fetch search results
     */
    searchResultsUrl: PropTypes.string,
};
